/**
 * Avertissements d'un calcul abouti (#975), tels que l'API les rend (`latestRun.warnings.items`) :
 * sans doublon, rouges d'abord, et seulement pour une tournée faisable (statut d'écran `feasible` :
 * une tournée validée relève de #982, un calcul en échec garde ses lignes de diagnostic). Rouge :
 * la bascule en vigueur est refusée ; orange : à vérifier. Ce module ne fait que les répartir :
 * chacun dans l'onglet du détail qui le concerne, sur le lieu concerné quand il en a un. Aucun JSX,
 * testé par `run-warnings.test.mjs`.
 */

import type {
  RunDiagnostic,
  SimulationDetailLatestRun,
  SimulatorListItem,
} from '~/lib/logistics-simulations-query'
import { pluralize } from '~/lib/utils'
import type { SimulationDetailTab } from '~/routes/_authenticated/logistics/-search-schema'
import { placeKey, type ScenarioPlace } from './scenario-view'

export type RunWarning = NonNullable<SimulationDetailLatestRun['warnings']>['items'][number]

export type RunWarningLevel = RunWarning['level']

/**
 * Lieu d'un diagnostic : seuls ceux-là se rattachent à une étape ou à un repère. Une boutique en
 * projet (#941) est bien une étape du trajet, mais le rattachement de ses avertissements attend
 * son marqueur sur les cartes (autre Sous-tâche) : ils restent sous le message de statut.
 */
type PlaceSubject = Omit<ScenarioPlace, 'type'> & {
  type: Exclude<ScenarioPlace['type'], 'store_project'>
}

/**
 * Onglet de chaque avertissement : la charge des boutiques au Trajet (étape et repère de la
 * boutique), la charge de la tournée aux Contraintes (carte « Camions et capacité »), les créneaux
 * aux Heures (ligne du lieu). Un code absent reste sous le message de statut.
 */
const TAB_OF_CODE: Readonly<Partial<Record<RunDiagnostic['code'], SimulationDetailTab>>> = {
  store_projection_zero: 'trajet',
  store_projection_missing: 'trajet',
  store_projection_partial: 'trajet',
  projection_run_missing: 'contraintes',
  product_colisage_missing: 'contraintes',
  extra_windows_ignored: 'heures',
}

export const STATUS_LABEL_TO_CHECK = 'À vérifier'

/** Avertissements du dernier calcul ; aucun sans calcul ou sans avertissement. */
export const runWarningsOf = (
  latestRun: Pick<SimulationDetailLatestRun, 'warnings'> | null,
): readonly RunWarning[] => latestRun?.warnings?.items ?? []

export const worstRunWarningLevelOf = (
  warnings: readonly { level: RunWarningLevel }[],
): RunWarningLevel | null => {
  if (warnings.length === 0) return null
  return warnings.some((warning) => warning.level === 'red') ? 'red' : 'orange'
}

const runWarningTabOf = (warning: RunWarning): SimulationDetailTab | null =>
  TAB_OF_CODE[warning.code] ?? null

export const runWarningsOfTab = (
  warnings: readonly RunWarning[],
  tab: SimulationDetailTab | null,
) => warnings.filter((warning) => runWarningTabOf(warning) === tab)

/** Pastille de chaque onglet : le niveau du plus grave de ses avertissements. */
export const tabWarningLevelsOf = (
  warnings: readonly RunWarning[],
): Partial<Record<SimulationDetailTab, RunWarningLevel>> => {
  const levels: Partial<Record<SimulationDetailTab, RunWarningLevel>> = {}
  for (const warning of warnings) {
    const tab = runWarningTabOf(warning)
    if (tab !== null && levels[tab] !== 'red') levels[tab] = warning.level
  }
  return levels
}

export const placeSubjectOf = (warning: RunWarning): PlaceSubject | null => {
  if (warning.subject === undefined) return null
  const { type, id } = warning.subject
  return (type === 'central_kitchen' || type === 'hub' || type === 'store') &&
    typeof id === 'number'
    ? { type, id }
    : null
}

/** Avertissements rattachés à un lieu, par clé de lieu (`placeKey`, `store-<id>`, `hub-<id>`). */
const runWarningsByPlaceOf = (warnings: readonly RunWarning[]): Map<string, RunWarning[]> => {
  const byPlace = new Map<string, RunWarning[]>()
  for (const warning of warnings) {
    const subject = placeSubjectOf(warning)
    if (subject === null) continue
    const key = placeKey(subject)
    byPlace.set(key, [...(byPlace.get(key) ?? []), warning])
  }
  return byPlace
}

/** Repères de la carte : le niveau du plus grave des avertissements de chaque lieu. */
export const placeWarningLevelsOf = (
  warnings: readonly RunWarning[],
): Map<string, RunWarningLevel> => {
  const levels = new Map<string, RunWarningLevel>()
  for (const warning of warnings) {
    const subject = placeSubjectOf(warning)
    if (subject === null) continue
    const key = placeKey(subject)
    if (levels.get(key) !== 'red') levels.set(key, warning.level)
  }
  return levels
}

/**
 * Avertissements d'un lieu à son premier passage seulement : un lieu peut revenir dans les lignes
 * d'un onglet (deux camions, rupture de charge puis retour). Appelée dans l'ordre des lignes.
 */
export const firstVisitRunWarningsOf = (warnings: readonly RunWarning[]) => {
  const byPlace = runWarningsByPlaceOf(warnings)
  const shown = new Set<string>()
  return (place: ScenarioPlace): RunWarning[] => {
    const key = placeKey(place)
    if (shown.has(key)) return []
    shown.add(key)
    return byPlace.get(key) ?? []
  }
}

/**
 * Avertissements d'un onglet qu'aucune de ses lignes n'affiche (sans lieu, ou lieu absent du
 * scénario) : l'onglet les montre en tête, pour qu'aucun ne se perde.
 */
export const unplacedRunWarningsOf = (
  warnings: readonly RunWarning[],
  shownPlaceKeys: ReadonlySet<string>,
): RunWarning[] =>
  warnings.filter((warning) => {
    const subject = placeSubjectOf(warning)
    return subject === null || !shownPlaceKeys.has(placeKey(subject))
  })

/** Message du statut d'une tournée faisable avec avertissements. */
export const runWarningStatusMessageOf = (warnings: readonly RunWarning[]): string => {
  const total = pluralize(warnings.length, 'point à vérifier', 'points à vérifier')
  const redCount = warnings.filter((warning) => warning.level === 'red').length
  return redCount === 0
    ? `Tournée calculée, ${total} : le détail est dans les onglets marqués.`
    : `Tournée calculée, ${total}, dont ${pluralize(redCount, 'bloquant')} pour l’entrée en vigueur (en rouge) : le détail est dans les onglets marqués.`
}

/**
 * Statut d'une tournée de la liste : « À vérifier » et le nombre de points pour une tournée
 * faisable avec avertissements (l'API ne rend `warnings` que pour celles-là), `null` sinon.
 */
export const listWarningOf = (
  item: Pick<SimulatorListItem, 'warnings'>,
): { level: RunWarningLevel; label: string } | null =>
  item.warnings === null
    ? null
    : {
        level: item.warnings.level,
        label: `${STATUS_LABEL_TO_CHECK} · ${pluralize(item.warnings.count, 'point')}`,
      }
